import { LoadingOverlay, Modal, Stack } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useParams } from 'react-router-dom';
import { SmallModal } from '../../../components/SmallModal';
import { Title } from '../../../components/Title';
import { FormButtons } from '../../../components/form/FormButtons';
import { mutate } from '../../../util/api/mutate';
import { query } from '../../../util/api/query';
import type { SelectableBranchDto } from '../../../util/types/SelectableBranchDto';
import { useUpsertBranchForm } from '../hooks/useNewBranchForm';
import type { NewBranchDto } from '../types/BranchTypes';
import { UpsertBranchForm } from './UpsertBranchComponents';

interface EditBranchModalProps {
	close: () => void;
	opened: boolean;
	updateValueInTable: (editBranch: NewBranchDto) => void;
	editBranch: NewBranchDto;
}

export function EditBranchModal({
	close,
	opened,
	updateValueInTable,
	editBranch,
}: EditBranchModalProps) {
	const { organisation } = useParams();
	const { form, inputs } = useUpsertBranchForm(editBranch);

	const [
		showSuccessModal,
		{ open: openSuccessModal, close: closeSuccessModal },
	] = useDisclosure(false);

	const pharmaciesQuery = query<SelectableBranchDto>(
		`${organisation}/branches/options`,
	);

	const editBranchMutation = mutate<NewBranchDto>(
		`${organisation}/branches/edit`,
		inputs,
	);

	const cancelClose = () => {
		close();
		closeSuccessModal();
		form.reset();
	};

	const successClose = () => {
		pharmaciesQuery.remove();
		pharmaciesQuery.refetch();
		
		form.setInitialValues(inputs);
		updateValueInTable(inputs);
		cancelClose();
	};

	return (
		<>
			<Modal
				opened={opened}
				onClose={cancelClose}
				title={<Title label={'Edit Branch'} />}
				w={100}
				withCloseButton={true}
				size={'lg'}
				centered
			>
				<SmallModal
					isOpen={showSuccessModal}
					close={successClose}
					title={'Branch Edited!'}
					body={
						'A branch has been successfully edited. Click OK to continue...'
					}
				/>
				<LoadingOverlay visible={editBranchMutation.isLoading} />
				<Stack w={'100%'} align={'center'}>
					<form
						onSubmit={form.onSubmit(async () => {
							await editBranchMutation.mutateAsync().then((response) => {
								if (response.isValid) {
									openSuccessModal();
								}
							});
						})}
					>
						<UpsertBranchForm
							pharmaciesQuery={pharmaciesQuery}
							form={form}
							inputs={inputs}
							area={inputs.area}
						/>
						<FormButtons close={cancelClose} />
					</form>
				</Stack>
			</Modal>
		</>
	);
}
